import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { api } from '@/shared/services/api';
import {
  addPrices,
  calculateMarginPercent,
  convertKRWtoUSD,
  roundByCurrency,
} from '@/shared/utils/priceUtils';

import {
  Artist,
  FormData,
  KeywordGroup,
  ProductBasicInfo,
  ProductDetail,
  ProductOption,
  ProductOptionsInfo,
  ProductPrice,
  ProductSidebarInfo,
} from '../types/productTypes';

const generateTempUuid = () => {
  return `temp-${Math.random().toString(36).substr(2, 9)}`;
};

const getStatusId = (status: string): number => {
  const statusMap: Record<string, number> = {
    active: 1,
    closed: 2,
    draft: 3,
  };
  return statusMap[status] || 1;
};

const getStatusName = (statusId: number): string => {
  const statusMap: Record<number, string> = {
    1: 'active',
    2: 'closed',
    3: 'draft',
  };
  return statusMap[statusId] || 'active';
};

const convertDateToUTC = (dateString: string): string => {
  if (!dateString) return '';

  if (dateString.includes('T') && dateString.includes('Z')) {
    return dateString;
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) {
    return '';
  }

  try {
    const kstDate = new Date(`${dateString}T00:00:00+09:00`);
    if (isNaN(kstDate.getTime())) {
      return '';
    }
    return kstDate.toISOString();
  } catch (error) {
    return '';
  }
};

const isTempUuid = (uuid: string): boolean => {
  return uuid.startsWith('temp-');
};

const removeEmptyFields = (payload: any, fieldsToCheck: string[]): any => {
  const result = { ...payload };

  fieldsToCheck.forEach((field) => {
    if (
      result[field] === '' ||
      result[field] === null ||
      result[field] === undefined
    ) {
      delete result[field];
    }
  });

  return result;
};

export interface OptionInputs {
  optionNameKey1: string;
  optionNameValue1: string;
  optionNameKey2: string;
  optionNameValue2: string;
  supplyPrice: string;
  profit: string;
  weight: string;
  barcode: string;
  stock: string;
  minimumOrderQuantity: string;
}

export interface SelectedOptionPrices {
  kr: ProductPrice | null;
  us: ProductPrice | null;
}

interface ProductFormState {
  formData: FormData;
  selectedOptionPrices: SelectedOptionPrices;
  optionInputs: OptionInputs;
  isAddMode: boolean;
  recentlyAddedOptions: string[];
  deletedOptionUuids: string[];
  s3ImageUrlArray: string[];
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  navigate: ((to: string) => void) | null;
}

interface ProductFormActions {
  updateBasicInfo: (updates: Partial<ProductBasicInfo>) => void;
  setTitle: (title: string) => void;
  setDescription: (description: string) => void;
  setSKU: (sku: string) => void;
  setHsCode: (hsCode: string) => void;
  setThumbnails: (thumbnails: string[]) => void;
  setStatus: (statusName: string) => void;
  updateSidebarInfo: (updates: Partial<ProductSidebarInfo>) => void;
  setCategory: (categoryName: string) => void;
  setCategoryWithId: (categoryId: number, categoryName: string) => void;
  setVendor: (vendor: { id: number; vendor: string }) => void;
  addArtist: (artist: Artist) => void;
  removeArtist: (artistId: number) => void;
  setTag: (tagId: number, tagName: string, isSelected: boolean) => void;
  removeTag: (tagId: number) => void;
  addKeywordGroup: (keywordGroup: KeywordGroup) => void;
  removeKeywordGroup: (keywordGroupId: number) => void;
  setReleaseDate: (date: string) => void;
  setSalesStartDate: (date: string) => void;
  setSalesEndDate: (date: string) => void;
  updateOptionsInfo: (updates: Partial<ProductOptionsInfo>) => void;
  selectOption: (option: ProductOption) => void;
  deleteOption: (optionUuid: string) => void;
  updateOptionInputs: (field: keyof OptionInputs, value: string) => void;
  resetOptionInputs: () => void;
  loadOptionToInputs: (option: ProductOption) => void;
  setAddMode: () => void;
  editOption: (option: ProductOption) => void;
  copyOption: (optionUuid: string) => void;
  updateDefaultOption: () => void;
  clearRecentlyAdded: (optionUuid: string) => void;
  resetOptionForm: () => void;
  setDefaultOption: (optionUuid: string) => void;
  updateSelectedOptionPrices: (option: ProductOption) => void;
  updatePriceTable: (
    nationCode: 'KR' | 'US',
    field: 'supplyPrice' | 'profitPrice' | 'comparePrice',
    value: string,
  ) => void;
  applyPrice: () => void;
  saveOption: () => Promise<void>;
  loadProduct: (productId: string) => Promise<void>;
  saveProduct: (isCreateMode: boolean, productId?: string) => Promise<void>;
  setS3ImageUrlArray: (urls: string[]) => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setError: (error: Error | null) => void;
  resetForm: () => void;
  formatDateForInput: (dateString: string) => string;
  formatPrice: (price: number | null | undefined) => string;
  setNavigate: (navigate: (to: string) => void) => void;
  validateFormData: () => { isValid: boolean; errors: string[] };
  buildApiPayload: (isCreateMode: boolean) => any;
}

const initialFormData: FormData = {
  basicInfo: {
    title: '',
    thumbnailUrlArray: [],
    description: '',
    SKU: '',
    hsCode: '',
    status: { statusId: 1, statusName: 'active' },
  },
  sidebarInfo: {
    category: { categoryId: 0, categoryName: '' },
    artistList: [],
    vendor: { id: 0, vendor: '' },
    tagList: [],
    keywordGroupList: [],
    releaseDate: '',
    salesStartAt: '',
    salesEndAt: '',
  },
  optionsInfo: {
    productOptionList: [],
    selectedOption: null,
  },
};

const initialOptionInputs: OptionInputs = {
  optionNameKey1: '',
  optionNameValue1: '',
  optionNameKey2: '',
  optionNameValue2: '',
  supplyPrice: '',
  profit: '',
  weight: '',
  barcode: '',
  stock: '',
  minimumOrderQuantity: '',
};

const initialSelectedPrices: SelectedOptionPrices = {
  kr: null,
  us: null,
};

type ProductFormStore = ProductFormState & ProductFormActions;

export const useProductFormStore = create<ProductFormStore>()(
  devtools(
    (set, get) => ({
      formData: initialFormData,
      selectedOptionPrices: initialSelectedPrices,
      optionInputs: initialOptionInputs,
      isAddMode: true,
      recentlyAddedOptions: [],
      deletedOptionUuids: [],
      s3ImageUrlArray: [],
      isLoading: false,
      isSaving: false,
      error: null,
      navigate: null,

      updateBasicInfo: (updates) => {
        set((state) => ({
          formData: {
            ...state.formData,
            basicInfo: { ...state.formData.basicInfo, ...updates },
          },
        }));
      },

      setTitle: (title) => {
        get().updateBasicInfo({ title });
      },

      setDescription: (description) => {
        get().updateBasicInfo({ description });
      },

      setSKU: (SKU) => {
        get().updateBasicInfo({ SKU });
      },

      setHsCode: (hsCode) => {
        get().updateBasicInfo({ hsCode });
      },

      setThumbnails: (thumbnailUrlArray) => {
        get().updateBasicInfo({ thumbnailUrlArray });
      },

      setStatus: (statusName) => {
        const statusId = getStatusId(statusName);
        get().updateBasicInfo({
          status: { statusId, statusName },
        });
      },

      updateSidebarInfo: (updates) => {
        set((state) => ({
          formData: {
            ...state.formData,
            sidebarInfo: { ...state.formData.sidebarInfo, ...updates },
          },
        }));
      },

      setCategory: (categoryName) => {
        const { formData } = get();
        get().updateSidebarInfo({
          category: { ...formData.sidebarInfo.category, categoryName },
        });
      },

      setCategoryWithId: (categoryId, categoryName) => {
        get().updateSidebarInfo({
          category: { categoryId, categoryName },
        });
      },

      setVendor: (vendor) => {
        get().updateSidebarInfo({
          vendor: { id: vendor.id, vendor: vendor.vendor },
        });
      },

      addArtist: (artist) => {
        const { formData } = get();
        const currentArtists = formData.sidebarInfo.artistList;

        const isAlreadySelected = currentArtists.some(
          (existingArtist) => existingArtist.artistId === artist.artistId,
        );

        if (!isAlreadySelected) {
          const updatedArtists = [...currentArtists, artist];
          get().updateSidebarInfo({ artistList: updatedArtists });
        }
      },

      removeArtist: (artistId) => {
        const { formData } = get();
        const updatedArtists = formData.sidebarInfo.artistList.filter(
          (artist) => artist.artistId !== artistId,
        );
        get().updateSidebarInfo({ artistList: updatedArtists });
      },

      setTag: (tagId, tagName, isSelected) => {
        const { formData } = get();
        const currentTags = formData.sidebarInfo.tagList;

        if (isSelected) {
          const isAlreadySelected = currentTags.some(
            (tag) => tag.tagId === tagId,
          );
          if (!isAlreadySelected) {
            const updatedTags = [...currentTags, { tagId, tagName }];
            get().updateSidebarInfo({ tagList: updatedTags });
          }
        } else {
          const updatedTags = currentTags.filter((tag) => tag.tagId !== tagId);
          get().updateSidebarInfo({ tagList: updatedTags });
        }
      },

      removeTag: (tagId) => {
        const { formData } = get();
        const updatedTags = formData.sidebarInfo.tagList.filter(
          (tag) => tag.tagId !== tagId,
        );
        get().updateSidebarInfo({ tagList: updatedTags });
      },

      addKeywordGroup: (keywordGroup) => {
        const { formData } = get();
        const currentKeywordGroups = formData.sidebarInfo.keywordGroupList;

        const isAlreadySelected = currentKeywordGroups.some(
          (existingKeywordGroup) =>
            existingKeywordGroup.keywordGroupId === keywordGroup.keywordGroupId,
        );

        if (!isAlreadySelected) {
          const updatedKeywordGroups = [...currentKeywordGroups, keywordGroup];
          get().updateSidebarInfo({ keywordGroupList: updatedKeywordGroups });
        }
      },

      removeKeywordGroup: (keywordGroupId) => {
        const { formData } = get();
        const updatedKeywordGroups =
          formData.sidebarInfo.keywordGroupList.filter(
            (group) => group.keywordGroupId !== keywordGroupId,
          );
        get().updateSidebarInfo({ keywordGroupList: updatedKeywordGroups });
      },

      setReleaseDate: (releaseDate) => {
        get().updateSidebarInfo({ releaseDate });
      },

      setSalesStartDate: (salesStartAt) => {
        get().updateSidebarInfo({ salesStartAt });
      },

      setSalesEndDate: (salesEndAt) => {
        get().updateSidebarInfo({ salesEndAt });
      },

      updateOptionsInfo: (updates) => {
        set((state) => ({
          formData: {
            ...state.formData,
            optionsInfo: { ...state.formData.optionsInfo, ...updates },
          },
        }));
      },

      selectOption: (option) => {
        get().updateOptionsInfo({ selectedOption: option });
        get().updateSelectedOptionPrices(option);
        get().loadOptionToInputs(option);
        set({ isAddMode: false });
      },

      deleteOption: (optionUuid) => {
        const { formData, deletedOptionUuids } = get();
        const currentOptions = formData.optionsInfo.productOptionList;
        const optionToDelete = currentOptions.find(
          (option) => option.optionUuid === optionUuid,
        );

        const visibleOptions = currentOptions.filter(
          (option) =>
            !deletedOptionUuids.includes(option.optionUuid) &&
            option.optionUuid !== optionUuid,
        );

        if (visibleOptions.length === 0) {
          alert('There must be at least one option.');
          return;
        }

        if (optionToDelete && !isTempUuid(optionUuid)) {
          set((state) => ({
            deletedOptionUuids: [...state.deletedOptionUuids, optionUuid],
          }));
        }

        const updatedOptions = currentOptions.filter(
          (option) => option.optionUuid !== optionUuid,
        );

        if (optionToDelete?.isDefault && updatedOptions.length > 0) {
          const firstVisibleOption = updatedOptions.find(
            (option) => !deletedOptionUuids.includes(option.optionUuid),
          );
          if (firstVisibleOption) {
            firstVisibleOption.isDefault = true;
          }
        }

        get().updateOptionsInfo({ productOptionList: updatedOptions });

        if (formData.optionsInfo.selectedOption?.optionUuid === optionUuid) {
          const newSelectedOption =
            updatedOptions.find(
              (option) => !deletedOptionUuids.includes(option.optionUuid),
            ) || null;

          if (newSelectedOption) {
            get().selectOption(newSelectedOption);
          } else {
            get().setAddMode();
          }
        }

        set((state) => ({
          recentlyAddedOptions: state.recentlyAddedOptions.filter(
            (uuid) => uuid !== optionUuid,
          ),
        }));
      },

      updateOptionInputs: (field, value) => {
        set((state) => ({
          optionInputs: { ...state.optionInputs, [field]: value },
        }));
      },

      resetOptionInputs: () => {
        set({ optionInputs: initialOptionInputs });
      },

      loadOptionToInputs: (option) => {
        const krPrice = option.productPriceList.find(
          (price) => price.nationCode2 === 'KR',
        );

        set({
          optionInputs: {
            optionNameKey1: option.optionNameKey1 || '',
            optionNameValue1: option.optionNameValue1 || '',
            optionNameKey2: option.optionNameKey2 || '',
            optionNameValue2: option.optionNameValue2 || '',
            supplyPrice: krPrice?.supplyPrice?.toString() || '',
            profit: krPrice?.profitPrice?.toString() || '',
            weight: option.weight?.toString() || '',
            barcode: option.barcode || '',
            stock: option.stock?.toString() || '',
            minimumOrderQuantity: option.minimumOrderQuantity?.toString() || '',
          },
        });
      },

      setAddMode: () => {
        set({
          isAddMode: true,
          optionInputs: initialOptionInputs,
          selectedOptionPrices: initialSelectedPrices,
        });
        get().updateOptionsInfo({ selectedOption: null });
      },

      editOption: (option) => {
        set({ isAddMode: false });
        get().selectOption(option);
        get().setDefaultOption(option.optionUuid);
      },

      copyOption: (optionUuid) => {
        const { formData } = get();
        const optionToCopy = formData.optionsInfo.productOptionList.find(
          (option) => option.optionUuid === optionUuid,
        );

        if (optionToCopy) {
          get().loadOptionToInputs(optionToCopy);
          get().updateSelectedOptionPrices(optionToCopy);
          set({ isAddMode: true });
          get().updateOptionsInfo({ selectedOption: null });
        }
      },

      setDefaultOption: (optionUuid) => {
        const { formData } = get();
        const currentOptions = formData.optionsInfo.productOptionList;

        const updatedOptions = currentOptions.map((option) => ({
          ...option,
          isDefault: option.optionUuid === optionUuid,
        }));

        get().updateOptionsInfo({ productOptionList: updatedOptions });
      },

      updateDefaultOption: () => {
        const { formData, deletedOptionUuids } = get();
        const options = formData.optionsInfo.productOptionList;

        const visibleOptions = options.filter(
          (option) => !deletedOptionUuids.includes(option.optionUuid),
        );

        const hasDefault = visibleOptions.some((option) => option.isDefault);
        if (!hasDefault && visibleOptions.length > 0) {
          const updatedOptions = [...options];
          const firstVisibleIndex = updatedOptions.findIndex(
            (option) => !deletedOptionUuids.includes(option.optionUuid),
          );
          if (firstVisibleIndex !== -1) {
            updatedOptions[firstVisibleIndex] = {
              ...updatedOptions[firstVisibleIndex],
              isDefault: true,
            };
            get().updateOptionsInfo({ productOptionList: updatedOptions });
          }
        }
      },

      clearRecentlyAdded: (optionUuid) => {
        set((state) => ({
          recentlyAddedOptions: state.recentlyAddedOptions.filter(
            (uuid) => uuid !== optionUuid,
          ),
        }));
      },

      resetOptionForm: () => {
        set({
          optionInputs: initialOptionInputs,
          selectedOptionPrices: initialSelectedPrices,
        });
        get().updateOptionsInfo({ selectedOption: null });
      },

      updateSelectedOptionPrices: (option) => {
        const krPrice =
          option.productPriceList.find((price) => price.nationCode2 === 'KR') ||
          null;
        const usPrice =
          option.productPriceList.find((price) => price.nationCode2 === 'US') ||
          null;

        set({ selectedOptionPrices: { kr: krPrice, us: usPrice } });
      },

      updatePriceTable: (nationCode, field, value) => {
        const numValue = parseFloat(value) || 0;
        const currency = nationCode === 'KR' ? 'KRW' : 'USD';
        const roundedValue = roundByCurrency(numValue, currency);

        set((state) => {
          const updated = { ...state.selectedOptionPrices };
          const priceKey = nationCode.toLowerCase() as 'kr' | 'us';

          if (updated[priceKey]) {
            updated[priceKey] = {
              ...updated[priceKey]!,
              [field]: roundedValue,
            };

            if (field === 'supplyPrice' || field === 'profitPrice') {
              const supply =
                field === 'supplyPrice'
                  ? roundedValue
                  : updated[priceKey]!.supplyPrice || 0;
              const profit =
                field === 'profitPrice'
                  ? roundedValue
                  : updated[priceKey]!.profitPrice || 0;

              const totalPrice = addPrices(supply, profit, currency);
              const marginPercent = calculateMarginPercent(profit, totalPrice);

              updated[priceKey]!.totalPrice = totalPrice;
              updated[priceKey]!.marginPercent = marginPercent;
            }
          }

          return { selectedOptionPrices: updated };
        });
      },

      applyPrice: () => {
        const { optionInputs, selectedOptionPrices } = get();
        const supplyPrice = parseFloat(optionInputs.supplyPrice) || 0;
        const profit = parseFloat(optionInputs.profit) || 0;

        const krSupplyPrice = roundByCurrency(supplyPrice, 'KRW');
        const krProfit = roundByCurrency(profit, 'KRW');
        const krTotalPrice = addPrices(krSupplyPrice, krProfit, 'KRW');
        const krMarginPercent = calculateMarginPercent(krProfit, krTotalPrice);

        const usdSupplyPrice = convertKRWtoUSD(krSupplyPrice);
        const usdProfit = convertKRWtoUSD(krProfit);
        const usdTotalPrice = addPrices(usdSupplyPrice, usdProfit, 'USD');
        const usdMarginPercent = calculateMarginPercent(
          usdProfit,
          usdTotalPrice,
        );

        set({
          selectedOptionPrices: {
            kr: {
              priceUuid: selectedOptionPrices.kr?.priceUuid || '',
              totalPrice: krTotalPrice,
              supplyPrice: krSupplyPrice,
              profitPrice: krProfit,
              marginPercent: krMarginPercent,
              comparePrice: selectedOptionPrices.kr?.comparePrice || 0,
              nationCode2: 'KR',
              createdAt: selectedOptionPrices.kr?.createdAt || '',
              updatedAt: selectedOptionPrices.kr?.updatedAt || '',
            },
            us: {
              priceUuid: selectedOptionPrices.us?.priceUuid || '',
              totalPrice: usdTotalPrice,
              supplyPrice: usdSupplyPrice,
              profitPrice: usdProfit,
              marginPercent: usdMarginPercent,
              comparePrice: selectedOptionPrices.us?.comparePrice || 0,
              nationCode2: 'US',
              createdAt: selectedOptionPrices.us?.createdAt || '',
              updatedAt: selectedOptionPrices.us?.updatedAt || '',
            },
          },
        });
      },

      saveOption: async () => {
        const {
          optionInputs,
          selectedOptionPrices,
          formData,
          setSaving,
          isAddMode,
        } = get();

        if (!optionInputs.optionNameKey1.trim()) {
          alert('Please enter the option name.');
          return;
        }
        if (!optionInputs.optionNameValue1.trim()) {
          alert('Please enter the option value.');
          return;
        }
        if (!optionInputs.stock || parseFloat(optionInputs.stock) < 0) {
          alert('유효한 재고를 입력해주세요.');
          return;
        }
        if (
          !optionInputs.weight ||
          parseFloat(optionInputs.weight) < 0 ||
          parseFloat(optionInputs.weight) > 999.99
        ) {
          alert('Weight must be between 0 and 999.99 kg.');
          return;
        }
        if (
          !optionInputs.supplyPrice ||
          parseFloat(optionInputs.supplyPrice) < 0
        ) {
          alert('Please enter a valid supply price.');
          return;
        }
        if (!optionInputs.profit || parseFloat(optionInputs.profit) < 0) {
          alert('Please enter a valid profit.');
          return;
        }

        if (!selectedOptionPrices.kr || !selectedOptionPrices.us) {
          alert('Please apply the price information first.');
          return;
        }

        setSaving(true);

        try {
          const optionUuid = isAddMode
            ? generateTempUuid()
            : formData.optionsInfo.selectedOption?.optionUuid ||
              generateTempUuid();

          const newOption: ProductOption = {
            productUuid: '',
            optionUuid,
            thumbnailUrl: '',
            title: '',
            isDefault: formData.optionsInfo.productOptionList.length === 0,
            optionNameKey1: optionInputs.optionNameKey1,
            optionNameValue1: optionInputs.optionNameValue1,
            ...(optionInputs.optionNameKey2 !== '' && {
              optionNameKey2: optionInputs.optionNameKey2,
            }),
            ...(optionInputs.optionNameValue2 !== '' && {
              optionNameValue2: optionInputs.optionNameValue2,
            }),
            stock: parseInt(optionInputs.stock, 10),
            barcode: optionInputs.barcode,
            weight: parseFloat(optionInputs.weight),
            isSoldOut: false,
            minimumOrderQuantity:
              parseInt(optionInputs.minimumOrderQuantity, 10) || 1,
            salesQuantity: 0,
            salesAmount: 0,
            totalSalesQuantity: 0,
            totalSalesAmount: 0,
            createdAt: '',
            productPriceList: [
              selectedOptionPrices.kr,
              selectedOptionPrices.us,
            ],
            minimumQuantity:
              parseInt(optionInputs.minimumOrderQuantity, 10) || 1,
          };

          let updatedOptions;

          if (isAddMode) {
            updatedOptions = [
              ...formData.optionsInfo.productOptionList,
              newOption,
            ];

            set((state) => ({
              recentlyAddedOptions: [...state.recentlyAddedOptions, optionUuid],
            }));
          } else {
            const existingOptionIndex =
              formData.optionsInfo.productOptionList.findIndex(
                (option) => option.optionUuid === optionUuid,
              );

            updatedOptions = [...formData.optionsInfo.productOptionList];
            updatedOptions[existingOptionIndex] = newOption;
          }

          get().updateOptionsInfo({
            productOptionList: updatedOptions,
          });

          get().resetOptionForm();
          get().setAddMode();

          alert('Option saved successfully.');
        } catch (err) {
          alert(`Error occurred while saving option: ${err}`);
        } finally {
          setSaving(false);
        }
      },

      validateFormData: () => {
        const { formData, deletedOptionUuids } = get();
        const errors: string[] = [];

        if (!formData.basicInfo.title.trim()) {
          errors.push('Please enter the product name.');
        }
        if (!formData.basicInfo.description.trim()) {
          errors.push('Please enter the product description.');
        }
        if (!formData.basicInfo.thumbnailUrlArray.length) {
          errors.push('Please upload at least one thumbnail image.');
        }
        if (!formData.basicInfo.hsCode.trim()) {
          errors.push('Please enter the HS code.');
        }

        const visibleOptions = formData.optionsInfo.productOptionList.filter(
          (option) => !deletedOptionUuids.includes(option.optionUuid),
        );

        if (visibleOptions.length === 0) {
          errors.push('At least one option is required.');
        }

        const defaultOptions = visibleOptions.filter(
          (option) => option.isDefault,
        );
        if (defaultOptions.length !== 1) {
          errors.push('Exactly one default option is required.');
        }

        for (const option of visibleOptions) {
          const krPrice = option.productPriceList.find(
            (p) => p.nationCode2 === 'KR',
          );
          const usPrice = option.productPriceList.find(
            (p) => p.nationCode2 === 'US',
          );

          if (!krPrice || !usPrice) {
            errors.push(
              `"${option.optionNameValue1}" must have both KR and US price information.`,
            );
          }
        }

        return {
          isValid: errors.length === 0,
          errors,
        };
      },

      buildApiPayload: (isCreateMode) => {
        const { formData, deletedOptionUuids } = get();

        const visibleOptions = formData.optionsInfo.productOptionList.filter(
          (option) => !deletedOptionUuids.includes(option.optionUuid),
        );

        const productOptionList = visibleOptions.map((option) => {
          const optionData: any = {
            isDefault: option.isDefault,
            optionNameKey1: option.optionNameKey1,
            optionNameValue1: option.optionNameValue1,
            ...(option.optionNameKey2 && {
              optionNameKey2: option.optionNameKey2,
            }),
            ...(option.optionNameValue2 && {
              optionNameValue2: option.optionNameValue2,
            }),
            minimumOrderQuantity: option.minimumOrderQuantity,
            stock: option.stock,
            barcode: option.barcode || '',
            weight: option.weight,
            productPriceList: option.productPriceList.map((price) => ({
              totalPrice: price.totalPrice,
              supplyPrice: price.supplyPrice,
              comparePrice: price.comparePrice,
              profitPrice: price.profitPrice,
              marginPercent: price.marginPercent,
              nationCode2: price.nationCode2,
            })),
          };

          if (!isCreateMode && !isTempUuid(option.optionUuid)) {
            optionData.optionUuid = option.optionUuid;
          }

          return optionData;
        });

        const basePayload: any = {
          title: formData.basicInfo.title,
          description: formData.basicInfo.description,
          thumbnailUrlArray: formData.basicInfo.thumbnailUrlArray,
          hsCode: formData.basicInfo.hsCode,
          categoryId: formData.sidebarInfo.category.categoryId || 0,
          statusId: formData.basicInfo.status.statusId,
          vendorId: formData.sidebarInfo.vendor.id || 0,
          productOptionList,
          artistList: formData.sidebarInfo.artistList.map((artist) => ({
            artistId: artist.artistId,
          })),
          tagList: formData.sidebarInfo.tagList.map((tag) => ({
            tagId: tag.tagId,
          })),
          keywordGroupList: formData.sidebarInfo.keywordGroupList.map(
            (group) => ({
              groupId: group.keywordGroupId,
            }),
          ),
        };

        if (formData.basicInfo.SKU && formData.basicInfo.SKU.trim() !== '') {
          basePayload.SKU = formData.basicInfo.SKU;
        }

        const releaseDate = convertDateToUTC(formData.sidebarInfo.releaseDate);
        if (releaseDate) {
          basePayload.releaseDate = releaseDate;
        }

        const salesStartAt = convertDateToUTC(
          formData.sidebarInfo.salesStartAt,
        );
        if (salesStartAt) {
          basePayload.salesStartAt = salesStartAt;
        }

        const salesEndAt = convertDateToUTC(formData.sidebarInfo.salesEndAt);
        if (salesEndAt) {
          basePayload.salesEndAt = salesEndAt;
        }

        const fieldsToCheck = [
          'SKU',
          'releaseDate',
          'salesStartAt',
          'salesEndAt',
        ];
        const cleanedPayload = removeEmptyFields(basePayload, fieldsToCheck);

        if (!isCreateMode) {
          return {
            ...cleanedPayload,
            deleteOptionList: deletedOptionUuids.filter(
              (uuid) => !isTempUuid(uuid),
            ),
          };
        }

        return cleanedPayload;
      },

      loadProduct: async (productId) => {
        const { setLoading, setError } = get();
        setLoading(true);
        setError(null);

        try {
          const response = await api.get<ProductDetail>(
            `/api/v1/product/detail/${productId}`,
          );
          const productData = response.data;

          if (productData) {
            const defaultOption =
              productData.productOptionList.find(
                (option) => option.isDefault,
              ) ||
              productData.productOptionList[0] ||
              null;

            set({
              formData: {
                basicInfo: {
                  title: productData.title,
                  thumbnailUrlArray:
                    JSON.parse(productData.thumbnailUrlArray) || [],
                  description: productData.description,
                  SKU: productData.SKU,
                  hsCode: productData.hsCode,
                  status: {
                    statusId: productData.status.statusId,
                    statusName: getStatusName(productData.status.statusId),
                  },
                },
                sidebarInfo: {
                  category: {
                    categoryId: productData.category.categoryId,
                    categoryName: productData.category.categoryName,
                  },
                  artistList: productData.artistList,
                  vendor: {
                    id: productData.vendor?.vendorId || 0,
                    vendor: productData.vendor?.vendorName || '',
                  },
                  tagList: productData.tagList,
                  keywordGroupList: productData.keywordGroupList,
                  releaseDate: productData.releaseDate,
                  salesStartAt: productData.salesStartAt,
                  salesEndAt: productData.salesEndAt,
                },
                optionsInfo: {
                  productOptionList: productData.productOptionList,
                  selectedOption: defaultOption,
                },
              },
              isAddMode: false,
              deletedOptionUuids: [],
            });

            if (defaultOption) {
              get().updateSelectedOptionPrices(defaultOption);
              get().loadOptionToInputs(defaultOption);
            }
          }
        } catch (err) {
          setError(err as Error);
        } finally {
          setLoading(false);
        }
      },

      saveProduct: async (isCreateMode, productId) => {
        const { setSaving, validateFormData, buildApiPayload, navigate } =
          get();

        const validation = validateFormData();
        if (!validation.isValid) {
          alert(validation.errors.join('\n'));
          return;
        }

        setSaving(true);

        try {
          const payload = buildApiPayload(isCreateMode);

          let response;
          if (isCreateMode) {
            response = await api.post<{ uuid: string }>(
              '/api/v1/product',
              payload,
            );

            alert('Product created successfully.');

            if (navigate && response.data?.uuid) {
              navigate(`/product/${response.data.uuid}`);
            }
          } else {
            if (!productId) {
              throw new Error('Product ID is required for update.');
            }

            response = await api.patch<{ productUuid: string }>(
              `/api/v1/product/${productId}`,
              payload,
            );

            alert('Product updated successfully.');
          }
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : 'An unknown error occurred';
          alert(
            `${isCreateMode ? 'Product creation' : 'Product update'} failed: ${errorMessage}`,
          );
        } finally {
          setSaving(false);
        }
      },

      setS3ImageUrlArray: (urls) => {
        set({ s3ImageUrlArray: urls });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setSaving: (saving) => {
        set({ isSaving: saving });
      },

      setError: (error) => {
        set({ error });
      },

      setNavigate: (navigate) => {
        set({ navigate });
      },

      resetForm: () => {
        set({
          formData: initialFormData,
          selectedOptionPrices: initialSelectedPrices,
          optionInputs: initialOptionInputs,
          isAddMode: true,
          recentlyAddedOptions: [],
          deletedOptionUuids: [],
          s3ImageUrlArray: [],
          isLoading: false,
          isSaving: false,
          error: null,
        });
      },

      formatDateForInput: (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().split('T')[0];
      },

      formatPrice: (price) => {
        if (price === null || price === undefined || price === 0) return '-';
        return price.toLocaleString();
      },
    }),
    {
      name: 'product-form-store',
    },
  ),
);
